/**
 * Demo Application Entry Point
 *
 * This is the main entry point for the demo application.
 * It sets up MSW (Mock Service Worker) with generated and static handlers
 * to intercept all API calls and return demo data.
 *
 * NO SERVER REQUIRED - all API responses are handled client-side by MSW.
 *
 * Modes:
 * - playback (default): Uses generated handlers + static handlers
 * - recording (legacy): Records API responses from live server (deprecated)
 */

import type { RequestHandler } from 'msw';
import { setupWorker } from 'msw/browser';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app';
import { initializeBigJs } from './big.config';
import './index.css';

// Import handlers
import { staticHandlers } from './static-handlers';
import { generatedHandlers } from './generated-handlers';

/**
 * Freeze the date to a fixed point in time for demo consistency.
 * This ensures all date-based calculations show the same results.
 */
const DEMO_DATE = new Date('2026-01-15T14:22:11.318Z');
const OriginalDate = globalThis.Date;

class FrozenDate extends OriginalDate {
  constructor(...args: ConstructorParameters<typeof Date> | []) {
    if (args.length === 0) {
      super(DEMO_DATE);
    } else {
      super(...args);
    }
  }

  static now(): number {
    return DEMO_DATE.getTime();
  }
}

// Replace global Date with frozen version
globalThis.Date = FrozenDate as DateConstructor;

console.log('[Demo] Date frozen to:', new Date().toISOString());

initializeBigJs();

/**
 * Get all MSW handlers for demo mode
 *
 * Combines:
 * 1. Generated handlers (from controller parsing)
 * 2. Static handlers (custom logic for auth, fx, etc.)
 *
 * Static handlers are added AFTER generated handlers so they can
 * act as catch-alls for unhandled routes.
 */
function getDemoHandlers(): RequestHandler[] {
  return [
    // Generated handlers first (specific routes)
    ...generatedHandlers,
    // Static handlers last (includes catch-alls)
    ...staticHandlers,
  ];
}

/**
 * Start MSW with appropriate handlers based on mode
 */
async function startMsw() {
  const handlers = getDemoHandlers();

  const worker = setupWorker(...handlers);


  await worker.start({
    onUnhandledRequest: 'bypass', // Don't warn about unhandled requests (static assets, etc.)
  });

  console.log('[Demo] MSW started with', handlers.length, 'handlers');
}

/**
 * Render the app
 */
startMsw().then(() => {

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
